import { FunctionComponent, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './editStaffDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Title from '@/components/_custom/Title/title'
import { StaffLevel } from '@/types/StaffLevel'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { useMutation } from '@apollo/client'
import { UPDATE_STAFF_LINK_LEVEL } from '@/modules/API/staff/staffLevels'
import { UPDATE_STAFF_RIGHTS } from '@/modules/API/daycare/staffLinkHandler'
import { StaffLevelRights, StaffWithLinks } from '@/types/Staff'
import { Daycare } from '@/types/Daycare'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import { useStaffPermissions } from '@/hooks/useStaffPermissions.ts'
import { mapPermissionsToGraphQLVars } from '@/modules/helper/permissionMapper.ts'
import { StaffRights } from '@/types/StaffRights.ts'
import PermissionsTable from './permissionsTable.tsx'
import { Role } from '@/types/Role.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface EditStaffDialogProps {
    open: boolean
    handleClose: () => void
    self: boolean
    staff: StaffWithLinks | null
    levels: StaffLevel[]
    currentDaycare?: Daycare
    refetchAllStaffList: () => Promise<void>
    canWrite: boolean
}

const EditStaffDialog: FunctionComponent<EditStaffDialogProps> = ({
    open,
    handleClose,
    staff,
    levels,
    self,
    refetchAllStaffList,
    currentDaycare,
    canWrite
}: EditStaffDialogProps) => {
    const { t } = useTranslation()
    const { user } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const staffRights: StaffLevelRights | null = useMemo<StaffLevelRights | null>((): StaffLevelRights | null => {
        if (!staff || !currentDaycare) return null
        return staff.staffLinks.find((link: StaffLevelRights): boolean => link.daycareId === currentDaycare.id) ?? null
    }, [staff, currentDaycare])

    const {
        permissions,
        staffLevel,
        setPermission,
        setStaffLevel,
        toggleAllRead,
        toggleAllWrite,
        allReadChecked,
        allWriteChecked,
        readIndeterminate,
        writeIndeterminate,
        isDirty
    } = useStaffPermissions({
        initialRights: staffRights,
        initialLevel: staffRights?.level?.toString() ?? 'no',
        addNewsWithoutValidationDisabled:
            currentDaycare?.haveDaycareGroup === true && !staffRights?.w_add_posts && user?.role === Role.staff
    })

    const levelsOptions: SelectOptionsDeprecated = useMemo(
        (): SelectOptionsDeprecated => [
            {
                options: [
                    { value: 'no', name: t('noStaffLevels') },
                    ...levels.map(
                        (level: StaffLevel): SelectOptionDeprecated => ({
                            value: level.id.toString(),
                            name: level.name
                        })
                    )
                ]
            }
        ],
        [levels, t]
    )

    const [updateStaffLevel] = useMutation(UPDATE_STAFF_LINK_LEVEL, {
        onCompleted: () => {
            showNotificationSuccess(t('staffLevelUpdated'))
            refetchAllStaffList()
        },
        onError: () => {
            showNotificationError(t('errorUpdatedStaffLevel'))
        }
    })

    const [updateStaffRights] = useMutation(UPDATE_STAFF_RIGHTS, {
        onCompleted: () => {
            showNotificationSuccess(t('staffAccessUpdated'))
            refetchAllStaffList()
        },
        onError: () => {
            showNotificationError(t('errorUpdatedStaffAccess'))
        }
    })

    const handleUpdate: () => void = (): void => {
        if (staffLevel !== (staffRights?.level?.toString() ?? 'no')) {
            updateStaffLevel({
                variables: {
                    staffLinkId: staff?.id,
                    daycareId: currentDaycare?.id,
                    staffLevelId: parseInt(staffLevel)
                }
            })
        }

        if (staff && !self) {
            const graphQLVars: StaffRights = mapPermissionsToGraphQLVars(permissions)
            updateStaffRights({
                variables: {
                    staff_id: staff.id,
                    daycare_id: currentDaycare?.id,
                    ...graphQLVars
                }
            })
        }

        handleClose()
    }

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('editStaff')} />
            <DialogBody className={styles.DialogBody}>
                {staff && (
                    <div className={styles.Container}>
                        <div className={styles.Title}>
                            <Title text={t('level')} />
                        </div>
                        <div className={styles.SelectContainer}>
                            <SelectUnique
                                selectOptions={levelsOptions}
                                selectValue={staffLevel}
                                setSelectValue={setStaffLevel}
                                formClassName={styles.SelectInput}
                                readOnly={!canWrite}
                            />
                        </div>
                    </div>
                )}
                <div className={styles.Container}>
                    <div className={styles.Title}>
                        <Title text={t('staffAccess')} />
                    </div>
                    <PermissionsTable
                        permissions={permissions}
                        setPermission={setPermission}
                        allReadChecked={allReadChecked}
                        allWriteChecked={allWriteChecked}
                        readIndeterminate={readIndeterminate}
                        writeIndeterminate={writeIndeterminate}
                        toggleAllRead={toggleAllRead}
                        toggleAllWrite={toggleAllWrite}
                        disabled={self || !canWrite}
                        currentDaycare={currentDaycare}
                        userRole={user?.role}
                        currentDaycareRightsAddNewsWithoutValidation={
                            !!user?.currentDaycareRights.canAddNewsWithoutValidation
                        }
                        showTitleInTable={false}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type='close' onClick={handleClose} className={styles.Button} />
                    {canWrite && (
                        <ButtonTextIcon
                            type='update'
                            onClick={handleUpdate}
                            className={styles.Button}
                            disabled={!isDirty}
                        />
                    )}
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditStaffDialog
